/**
 * Classroom room numbers, stored on `students.metadata->>'room_number'`.
 *
 * A room number is digits and nothing else: "1", "13", "013", "105" are all valid;
 * "r1", "room1009", "12A" are not. Leading zeros are meaningful to the people who write
 * them on a door, so the value is kept as a STRING — a numeric column or an `<input
 * type="number">` would silently turn "013" into 13 and lose how the school writes it.
 */

/** Digits only, at least one. Anchored, so nothing may surround them. */
export const ROOM_NUMBER_PATTERN = /^\d+$/;

/**
 * Longest room number accepted. Six digits is far beyond any real classroom and still
 * short enough that a mis-paste (a phone number, a student id) is rejected rather than
 * stored.
 */
export const ROOM_NUMBER_MAX_LENGTH = 6;

/** True when `value` is a well-formed room number. */
export function isValidRoomNumber(value: string): boolean {
  return ROOM_NUMBER_PATTERN.test(value) && value.length <= ROOM_NUMBER_MAX_LENGTH;
}

/**
 * What to keep as the user types: digits only, capped in length.
 *
 * Filtering during input is friendlier than rejecting on submit — a stray letter simply
 * never appears, so there is no error message to read and no valid-looking value to fix.
 */
export function sanitizeRoomNumberInput(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, ROOM_NUMBER_MAX_LENGTH);
}

/**
 * The value to persist, or `undefined` to leave the field unset.
 *
 * Room number is optional, so an empty box means "no room on file" and must clear the key
 * rather than store an empty string — `undefined` drops out of the JSON entirely.
 */
export function toStoredRoomNumber(raw: string | null | undefined): string | undefined {
  const digits = sanitizeRoomNumberInput(raw ?? '');
  return digits.length > 0 ? digits : undefined;
}

/**
 * Sort key for a room number.
 *
 * Numeric, so room 9 comes before room 10 — a plain string sort would put "10" first.
 * Rooms without a number sort last, since "unknown" is not a place in the building.
 */
export function roomSortKey(room: string | null | undefined): number {
  if (!room || !ROOM_NUMBER_PATTERN.test(room)) return Number.MAX_SAFE_INTEGER;
  return Number(room);
}

/**
 * The identity two room numbers are compared on for grouping: their numeric value.
 *
 * "013" and "13" are the same door written two ways, so they belong in one group. This is
 * deliberately unlike school names, which group on their exact spelling — there, two
 * similar strings may genuinely be two different schools, whereas here the values are
 * integers and equality is unambiguous.
 */
export function roomGroupKey(room: string): string {
  return ROOM_NUMBER_PATTERN.test(room) ? String(Number(room)) : room;
}
